import { useCallback } from 'react';
import useSWR from 'swr';

import { endpoints, fetcher } from 'src/utils/axios';
import { WebhookDTO, WebhookActionType } from 'src/types/dto/webhook.dto';
import { usePaginatedApi } from './use-paginated-api';
import { useUserPermissions } from './use-user-permissions';
import { PermissionKeys } from 'src/auth/types';

export function useAutomationCheck() {
  const {hasAnyPermission} = useUserPermissions();
  const hasPermission = hasAnyPermission([
    { permission: PermissionKeys.VIEW_WEBHOOKS, levels: ['GROUP', 'ALL'] },
  ]);

  console.log('[useAutomationCheck] VIEW_WEBHOOKS permission check:', hasPermission);

  if (!hasPermission) {
    return {
      webhooks: [],
      loading: false,
      error: null,
      hasAutomation: () => false,
      hasPreRequestAutomation: () => false,
      hasRequestAutomation: () => false,
      hasCancelRequestAutomation: () => false,
      refetch: () => {},
    };
  }

  const { data: requestWebhooks, isLoading: requestWebhooksLoading, mutate: requestWebhooksMutate, error: requestWebhooksError } = usePaginatedApi<WebhookDTO>({
    endpoint: endpoints.webhooks.list,
    paginationParams: { page: 1, take: 100 },
    additionalParams: {
      'action-types': 'REQUEST'
    }
  });
  const { data: preRequestWebhooks, isLoading: preRequestWebhooksLoading, mutate: preRequestWebhooksMutate, error: preRequestWebhooksError } = usePaginatedApi<WebhookDTO>({
    endpoint: endpoints.webhooks.list,
    paginationParams: { page: 1, take: 100 },
    additionalParams: {
      'action-types': 'PRE_REQUEST'
    }
  });
  const { data: cancelRequestWebhooks, isLoading: cancelRequestWebhooksLoading, mutate: cancelRequestWebhooksMutate, error: cancelRequestWebhooksError } = usePaginatedApi<WebhookDTO>({
    endpoint: endpoints.webhooks.list,
    paginationParams: { page: 1, take: 100 },
    additionalParams: {
      'action-types': 'CANCEL_REQUEST'
    }
  });
  const webhooks = [...requestWebhooks, ...preRequestWebhooks, ...cancelRequestWebhooks];
  const loading = requestWebhooksLoading || preRequestWebhooksLoading || cancelRequestWebhooksLoading;

  console.log('[useAutomationCheck] webhooks loaded:', { request: requestWebhooks.length, preRequest: preRequestWebhooks.length, cancelRequest: cancelRequestWebhooks.length, loading });

  const hasAutomation = useCallback((actionType: WebhookActionType) => {
    return webhooks.some(webhook =>
      webhook.action?.action === actionType &&
      webhook.action?.entityType === 'Activity'
    );
  }, [webhooks]);

  const hasPreRequestAutomation = useCallback(() => {
    return hasAutomation(WebhookActionType.PRE_REQUEST);
  }, [hasAutomation]);

  const hasRequestAutomation = useCallback(() => {
    return hasAutomation(WebhookActionType.REQUEST);
  }, [hasAutomation]);

  const hasCancelRequestAutomation = useCallback(() => {
    return hasAutomation(WebhookActionType.CANCEL_REQUEST);
  }, [hasAutomation]);

  return {
    webhooks,
    loading,
    error: requestWebhooksError || preRequestWebhooksError || cancelRequestWebhooksError,
    hasAutomation,
    hasPreRequestAutomation,
    hasRequestAutomation,
    hasCancelRequestAutomation,
    refetch: requestWebhooksMutate || preRequestWebhooksMutate || cancelRequestWebhooksMutate,
  };
}

